import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  AddressType,
  AreaType,
  BloodGroup,
  ContingentType,
  EducationDocumentType,
  EttIntubation,
  Gender,
  MedRecordType,
  MedServicePackageType,
  RhesusFactor,
  ScheduleOwnerType,
  ScheduleRestrictionType,
  SpecializationType,
  Weekday,
  ZoneType,
} from '../enums';
import { getKeyValues } from '../tools/enum-helper';
import { DischargeResult } from '../enums/discharge-type.enum';

@Injectable({ providedIn: 'root' })
export class EnumService {
  constructor(private translate: TranslateService) {}

  public getGenders() {
    return [
      { text: this.translate.instant('MALE'), value: Gender.Male },
      { text: this.translate.instant('FEMALE'), value: Gender.Female },
    ];
  }

  public getGenderText(gender: Gender): string {
    switch (gender) {
      case Gender.Male:
        return this.translate.instant('MALE');
      case Gender.Female:
        return this.translate.instant('FEMALE');
      default:
        return '';
    }
  }

  public getBloodGroups() {
    return [
      { text: this.translate.instant('FIRST_GROUP'), value: BloodGroup.O },
      { text: this.translate.instant('SECOND_GROUP'), value: BloodGroup.A },
      { text: this.translate.instant('THIRD_GROUP'), value: BloodGroup.B },
      { text: this.translate.instant('FOURTH_GROUP'), value: BloodGroup.AB },
    ];
  }

  public getRhesusFactors() {
    return [
      { text: this.translate.instant('RH_PLUS'), value: RhesusFactor.Plus },
      { text: this.translate.instant('RH_MINUS'), value: RhesusFactor.Minus },
    ];
  }

  public getAreaTypes() {
    return [
      { text: this.translate.instant('COUNTRY'), value: AreaType.Country },
      { text: this.translate.instant('REGION'), value: AreaType.Region },
      { text: this.translate.instant('CITY'), value: AreaType.City },
    ];
  }

  public getContingentTypes() {
    return [
      { text: this.translate.instant('FREE'), value: ContingentType.Free },
      { text: this.translate.instant('PAYING'), value: ContingentType.Paid },
    ];
  }

  public dischargeResults() {
    return [
      {
        text: this.translate.instant('RESULT_SUCCESS'),
        value: DischargeResult.Success,
      },
      {
        text: this.translate.instant('RESULT_DIED'),
        value: DischargeResult.Died,
      },
      {
        text: this.translate.instant('RESULT_MOVED'),
        value: DischargeResult.Moved,
      },
    ];
  }

  public getDischargeResult(type: DischargeResult): string {
    switch (type) {
      case DischargeResult.Success:
        return this.translate.instant('RESULT_SUCCESS');
      case DischargeResult.Died:
        return this.translate.instant('RESULT_DIED');
      case DischargeResult.Moved:
        return this.translate.instant('RESULT_MOVED');
      default:
        return '';
    }
  }

  public getWeekDays() {
    return [
      { text: this.translate.instant('MONDAY'), value: Weekday.Monday },
      { text: this.translate.instant('TUESDAY'), value: Weekday.Tuesday },
      { text: this.translate.instant('WEDNESDAY'), value: Weekday.Wednesday },
      { text: this.translate.instant('THURSDAY'), value: Weekday.Thursday },
      { text: this.translate.instant('FRIDAY'), value: Weekday.Friday },
      { text: this.translate.instant('SATURDAY'), value: Weekday.Saturday },
      { text: this.translate.instant('SUNDAY'), value: Weekday.Sunday },
    ];
  }

  public getAddressTypes() {
    return [
      {
        text: this.translate.instant('ADDRESS_TYPE.LIVING'),
        value: AddressType.Living,
      },
      {
        text: this.translate.instant('ADDRESS_TYPE.REGISTERED'),
        value: AddressType.Registered,
      },
      {
        text: this.translate.instant('ADDRESS_TYPE.MAILING'),
        value: AddressType.Mailing,
      },
      {
        text: this.translate.instant('ADDRESS_TYPE.BILLING'),
        value: AddressType.Billing,
      },
    ];
  }

  public getScheduleRestrictionTypes() {
    return [
      {
        text: this.translate.instant('SCHEDULE.RESTRICTION_TYPE.TIME_OFF'),
        value: ScheduleRestrictionType.TimeOff,
      },
      {
        text: this.translate.instant('SCHEDULE.RESTRICTION_TYPE.VACATION'),
        value: ScheduleRestrictionType.Vacation,
      },
      {
        text: this.translate.instant('SCHEDULE.RESTRICTION_TYPE.STICK_LEAVE'),
        value: ScheduleRestrictionType.StickLeave,
      },
      {
        text: this.translate.instant('SCHEDULE.RESTRICTION_TYPE.HOLIDAY'),
        value: ScheduleRestrictionType.Holiday,
      },
      {
        text: this.translate.instant('SCHEDULE.RESTRICTION_TYPE.OTHER'),
        value: ScheduleRestrictionType.Other,
      },
    ];
  }

  public getEducationDocumentTypes() {
    return [
      {
        text: this.translate.instant(
          'DOCTOR.EDUCATION_DOCUMENT_TYPE.HIGH_SCHOOL_DIPLOMA'
        ),
        value: EducationDocumentType.HighSchoolDiploma,
      },
      {
        text: this.translate.instant(
          'DOCTOR.EDUCATION_DOCUMENT_TYPE.COLLEGE_DIPLOMA'
        ),
        value: EducationDocumentType.CollegeDiploma,
      },
      {
        text: this.translate.instant(
          'DOCTOR.EDUCATION_DOCUMENT_TYPE.UNIVERSITY_DIPLOMA'
        ),
        value: EducationDocumentType.UniversityDiploma,
      },
      {
        text: this.translate.instant(
          'DOCTOR.EDUCATION_DOCUMENT_TYPE.CERTIFICATE'
        ),
        value: EducationDocumentType.Certificate,
      },
    ];
  }

  public getScheduleOwnerTypes() {
    return [
      {
        text: this.translate.instant('DOCTORS'),
        value: ScheduleOwnerType.Doctor,
      },
      {
        text: this.translate.instant('CABINETS'),
        value: ScheduleOwnerType.Doctor,
      },
    ];
  }

  public getSpecializationTypes() {
    return [
      {
        text: this.translate.instant('CHILDREN'),
        value: SpecializationType.Children,
      },
      {
        text: this.translate.instant('ADULT'),
        value: SpecializationType.Adult,
      },
      {
        text: this.translate.instant('EMERGENCY_ROOM'),
        value: SpecializationType.EmergencyRoom,
      },
      {
        text: this.translate.instant('COMBUSTIOLOGY'),
        value: SpecializationType.Combustiology,
      },
      {
        text: this.translate.instant('TOXICOLOGY'),
        value: SpecializationType.Toxicology,
      },
    ];
  }

  public getSpecializationTypeText(specializationType: SpecializationType): string {
    switch (specializationType) {
      case SpecializationType.Children:
        return this.translate.instant('CHILDREN');
      case SpecializationType.Adult:
        return this.translate.instant('ADULT');
      case SpecializationType.EmergencyRoom:
          return this.translate.instant('EMERGENCY_ROOM');
      case SpecializationType.Combustiology:
        return this.translate.instant('COMBUSTIOLOGY');
      case SpecializationType.Toxicology:
        return this.translate.instant('TOXICOLOGY');
      default:
        return '';
    }
  }

  public getZoneTypes() {
    return [
      {
        text: this.translate.instant('TRIAGE_RED'),
        value: ZoneType.Red,
      },
      {
        text: this.translate.instant('TRIAGE_YELLOW'),
        value: ZoneType.Yellow,
      },
      {
        text: this.translate.instant('TRIAGE_GREEN'),
        value: ZoneType.Green,
      },
    ];
  }

  public getZoneTypeText(zoneType: ZoneType): string {
    switch (zoneType) {
      case ZoneType.Red:
        return this.translate.instant('TRIAGE_RED');
      case ZoneType.Yellow:
        return this.translate.instant('TRIAGE_YELLOW');
      case ZoneType.Green:
        return this.translate.instant('TRIAGE_GREEN');
      default:
        return '';
    }
  }

  public getMedRecordTypes() {
    return getKeyValues(MedRecordType, (value) =>
      this.translate.instant(value.toUpperCase())
    );
  }
  public getMedServicePackageTypes() {
    return getKeyValues(MedServicePackageType, (value) =>
      this.translate.instant(value.toUpperCase())
    );
  }

  public getEttItubations() {
    return [
      {
        text: '1.0',
        value: EttIntubation.Value10,
      },
      {
        text: '1.5',
        value: EttIntubation.Value15,
      },
      {
        text: '2.0',
        value: EttIntubation.Value20,
      },
      {
        text: '2.5',
        value: EttIntubation.Value25,
      },
      {
        text: '3.0',
        value: EttIntubation.Value30,
      },
      {
        text: '3.5',
        value: EttIntubation.Value35,
      },
      {
        text: '4.0',
        value: EttIntubation.Value40,
      },
      {
        text: '4.5',
        value: EttIntubation.Value45,
      },
      {
        text: '5.0',
        value: EttIntubation.Value50,
      },
      {
        text: '5.5',
        value: EttIntubation.Value55,
      },
      {
        text: '6.0',
        value: EttIntubation.Value60,
      },
      {
        text: '6.5',
        value: EttIntubation.Value65,
      },
      {
        text: '7.0',
        value: EttIntubation.Value70,
      },
      {
        text: '7.5',
        value: EttIntubation.Value75,
      },
      {
        text: '8.0',
        value: EttIntubation.Value80,
      },
      {
        text: '8.5',
        value: EttIntubation.Value85,
      },
      {
        text: '9.0',
        value: EttIntubation.Value90,
      },
      {
        text: '9.5',
        value: EttIntubation.Value95,
      },
      {
        text: '10',
        value: EttIntubation.Value100,
      },
    ];
  }
}
